/**
 * EmailOctopus Service - Email List Management Integration
 * 
 * CREATED: Original implementation date unknown
 * UPDATED: Dec 2024 - Added Logger integration and CORS proxy support
 * WHO: Original author unknown; Updated by GitHub Copilot (Claude Sonnet 4.5)
 * WHY: Manage email subscriptions via EmailOctopus API
 * 
 * PURPOSE:
 * - Integrates with EmailOctopus email marketing platform
 * - Handles user unsubscribe requests
 * - Proxies API calls through backend to avoid CORS issues
 * - Uses Auth0 JWT token for authentication
 * 
 * API INTEGRATION:
 * - EmailOctopus API v1.6 (Legacy)
 * - Uses MD5 hash of lowercase email for contact identification
 * - All requests proxied through /api/v1/external/emailoctopus/* endpoints
 * 
 * BACKEND PROXY:
 * The frontend cannot call EmailOctopus API directly due to CORS restrictions.
 * All requests go through backend endpoints in server.js which:
 * 1. Receive email from frontend
 * 2. Convert to lowercase and hash with MD5
 * 3. Make actual API call to EmailOctopus
 * 4. Return results to frontend
 * 
 * AUTHENTICATION:
 * - Requires valid Auth0 JWT token
 * - Token passed in Authorization header
 * 
 * USAGE:
 * import EmailOctopusService from '@/services/EmailOctopusService'
 * 
 * // Unsubscribe user from email list
 * const token = await getAccessTokenSilently()
 * const result = await EmailOctopusService.unsubscribeUser(token, 'user@example.com')
 * if (result.successful) {
 *   console.log('User unsubscribed')
 * }
 * 
 * RESPONSE FORMAT:
 * {
 *   successful: boolean,
 *   message: string,
 *   data?: any (only on error)
 * }
 */

import Logger from '@/utils/Logger';

const axios = require('axios');
const log = Logger.create('EmailOctopusService', { showCaller: true, showLineNumbers: true });

class LocalEmailOctopusService {

	/**
	 * Unsubscribe a user from the EmailOctopus mailing list
	 * 
	 * @param {string} token - Auth0 JWT access token
	 * @param {string} email - User's email address to unsubscribe
	 * @returns {Promise<Object>} Result object with successful flag and message
	 * 
	 * @example
	 * const token = await getAccessTokenSilently()
	 * const result = await EmailOctopusService.unsubscribeUser(token, 'user@example.com')
	 * if (result.successful) {
	 *   console.log(result.message) // "User unsubscribed from EmailOctopus."
	 * }
	 */
	static unsubscribeUser(token, email) {

		let rm = {}
	   
		// Normalize email: lowercase and trim whitespace
		email = String(email).toLowerCase().trim();

		log.trace('Unsubscribing user with email:', email);

		return new Promise(async (resolve, reject) => {
			   
			// POST to backend proxy endpoint
			// Backend handles MD5 hashing and actual EmailOctopus API call
			axios.post('/api/v1/external/emailoctopus/unsubscribe', 
				{
					data: { email: email }
				},
			{
            			headers: {Authorization: `Bearer ${token}`}
        			}).then((response) => {
				 
				log.trace('EmailOctopus unsubscribe response:', response);

				// Check response status and build result object
				if(response.status === 200) {
					rm.successful = true
					rm.message = 'User unsubscribed from EmailOctopus.'	
				} else {
					rm.successful = false
					rm.message = 'Error unsubscribing user from EmailOctopus.  Code: ' 
						+ response.status
						+ ', Message: ' + response.statusText
						+ '.'
				}

				resolve(rm)

			}).catch((error) =>{

					log.error('Encountered an error:', error);
		
					// Return error details in result object
					rm.successful = false
					rm.data = error
		
					resolve(rm)
		
				  })
			   });
			}
		}

		export default LocalEmailOctopusService;
